/* eslint-disable no-console */
import axios from "axios";

// API configuration
// In development, requests go through webpack dev server proxy to avoid CORS
const API_BASE_URL = "/frontendapi";

// JWT access token — stored in module memory only, cleared on page reload.
// The httpOnly refresh cookie (set by the server) persists across reloads and
// is used by portal_refresh to issue a new access token silently.
let _accessToken = null;
let _refreshPromise = null;

export const setAccessToken = token => {
  _accessToken = token;
};
const clearAccessToken = () => {
  _accessToken = null;
};

// Exported so other modules (e.g. stageplayer-args) can read the current token
// and attach it to raw fetch() calls that bypass the axios instance.
export const getAccessToken = () => _accessToken;

// Create axios instance.
// withCredentials: true is kept globally so the httpOnly refresh cookie is
// automatically included on portal_refresh calls without special-casing.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

// Attach Bearer token to every outgoing request when available.
// In axios 1.x config.headers is an AxiosHeaders instance — mutate it directly
// rather than spreading into a plain object, which strips the class prototype
// and causes axios to silently ignore the header.
apiClient.interceptors.request.use(config => {
  if (_accessToken) {
    config.headers.set("Authorization", `Bearer ${_accessToken}`);
  }
  return config;
});

// Response interceptor — handles token expiry and session expiry.
// On 401 "token expired": silently refreshes using the httpOnly cookie and
// retries the original request. Uses a singleton promise so concurrent 401s
// don't each fire a refresh (which would rotate the cookie and break each other).
// On any other session-terminating 401: fires portal:session-expired.
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const msg =
      error.response?.data?.message || error.response?.data?.error || "";
    const lc = msg.toLowerCase();
    const failedAction = error.config?.params?.action;
    if (error.response?.status === 401) {
      // Check refresh-token expiry FIRST — its message contains 'token expired'
      // as a substring, so it must be matched before the access-token check.
      if (
        lc.includes("refresh token expired") ||
        lc.includes("session expired") ||
        lc.includes("not logged in") ||
        lc.includes("no active session")
      ) {
        clearAccessToken();
        window.dispatchEvent(new CustomEvent("portal:session-expired"));
      } else if (
        lc.includes("token expired") &&
        failedAction !== "portal_refresh"
      ) {
        // Never try to auto-refresh when portal_refresh itself fails — that
        // would either self-loop (awaiting its own promise) or fire a second
        // refresh that also fails, then dispatch session-expired spuriously.
        try {
          if (!_refreshPromise) {
            _refreshPromise = apiClient
              .post("", {}, { params: { action: "portal_refresh" } })
              .finally(() => {
                _refreshPromise = null;
              });
          }
          const refresh = await _refreshPromise;
          setAccessToken(refresh.data.accessToken);
          // Mutate the header on the existing config — same AxiosHeaders fix as
          // the request interceptor. Spreading loses the AxiosHeaders prototype.
          error.config.headers.set(
            "Authorization",
            `Bearer ${refresh.data.accessToken}`
          );
          return apiClient(error.config);
        } catch {
          clearAccessToken();
          window.dispatchEvent(new CustomEvent("portal:session-expired"));
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Handle API errors and normalize error responses
 * @param {Error} error - Axios error object
 * @returns {Error} Normalized error with message
 */
function handleApiError(error) {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    const message = data?.error || data?.message || `API error: ${status}`;
    const err = new Error(message);
    err.status = status;
    err.data = data;
    // Subtype for specific 403 account states
    if (status === 403) {
      const lc = message.toLowerCase();
      if (lc.includes("pending")) err.subtype = "pending";
      else if (lc.includes("disabled")) err.subtype = "disabled";
      else if (lc.includes("too many")) err.subtype = "ratelimit";
    }
    return err;
  }
  if (error.request) {
    // Request made but no response received
    return new Error("No response from server. Please check your connection.");
  }
  // Something else happened
  return new Error(error.message || "An unexpected error occurred");
}

/**
 * Human-readable labels for background task types returned by portal_project_status.
 * The backend queues these tasks automatically on upload (GPX/SVX/MP4) or via portal_scan_project.
 * Use these when rendering progress UI so the user sees "Converting GPS data" instead of "GPX_CONVERT".
 */
export const TASK_LABELS = {
  UPLOAD: "Uploading file",
  GPX_CONVERT: "Converting GPS data",
  FIT_CONVERT: "Converting fitness data",
  SVX_CONVERT: "Converting detections",
  CSV_CONVERT: "Converting CSV",
  MP4_EXTRACT: "Reading video timestamp",
  TRANSCODE: "Transcoding video",
  SCAN: "Scanning project",
  AUDIO_ALIGN: "Aligning audio",
  FR10_EXTRACT: "Extracting FaceReader data"
};

/**
 * Portal Authentication API
 * Based on marin-frontend-api portal endpoints
 */
export const portalApi = {
  /**
   * Create a new portal account
   * POST action=portal_create_account
   * @param {string} user - Username
   * @param {string} password - Password
   * @param {string} email - Email address
   * @returns {Promise<{status: number, message: string, username: string}>}
   */
  createAccount: async (user, password, email) => {
    try {
      const response = await apiClient.post(
        "",
        {
          user,
          password,
          email
        },
        {
          params: { action: "portal_create_account" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Authenticate a portal user and start a session
   * POST action=portal_login
   * @param {string} user - Username
   * @param {string} password - Password
   * @returns {Promise<{status: number, message: string, username: string}>}
   */
  login: async (user, password) => {
    try {
      const response = await apiClient.post(
        "",
        {
          user,
          password
        },
        {
          params: { action: "portal_login" }
        }
      );
      if (response.data.accessToken) {
        setAccessToken(response.data.accessToken);
      }
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Exchange the httpOnly refresh cookie for a new access token.
   * POST action=portal_refresh
   * Call on page load / new tab when no access token is in memory.
   * Uses the same singleton _refreshPromise as the response interceptor so that
   * a concurrent 401-triggered refresh and an explicit mount refresh never both
   * fire portal_refresh simultaneously (which would rotate the cookie twice and
   * cause the second caller to fail).
   * @returns {Promise<{status: number, accessToken: string, expiresIn: number}>}
   */
  refresh: async () => {
    try {
      if (!_refreshPromise) {
        _refreshPromise = apiClient
          .post("", {}, { params: { action: "portal_refresh" } })
          .finally(() => {
            _refreshPromise = null;
          });
      }
      const response = await _refreshPromise;
      if (response.data.accessToken) {
        setAccessToken(response.data.accessToken);
      }
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * End the portal session
   * POST action=portal_logout
   * @returns {Promise<{status: number, message: string}>}
   */
  logout: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        {
          params: { action: "portal_logout" }
        }
      );
      clearAccessToken();
      return response.data;
    } catch (error) {
      clearAccessToken(); // clear even if server call fails
      throw handleApiError(error);
    }
  },

  /**
   * Create a reset token for a user
   * POST action=portal_forgot_password
   * @param {string} user - Username
   * @returns {Promise<{status: number, message: string, resetToken: string}>}
   */
  forgotPassword: async user => {
    try {
      const response = await apiClient.post(
        "",
        {
          user
        },
        {
          params: { action: "portal_forgot_password" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all projects for the logged-in user
   * POST action=portal_list_projects
   * @returns {Promise<{status: number, message: string, projects: Array}>}
   */
  listProjects: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        {
          params: { action: "portal_list_projects" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a new project under the logged-in user's account
   * POST action=portal_add_project
   * @param {string} name - Project name
   * @param {string} description - Project description (optional)
   * @returns {Promise<{status: number, message: string, project: Object}>}
   */
  addProject: async (
    name,
    description = "",
    startDate = "",
    startTime = "",
    endDate = "",
    endTime = "",
    tags = [],
    wizard = "4steps"
  ) => {
    try {
      const response = await apiClient.post(
        "",
        {
          name,
          description,
          startDate,
          startTime,
          endDate,
          endTime,
          tags,
          wizard
        },
        {
          params: { action: "portal_add_project" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a project by ID
   * POST action=portal_remove_project
   * @param {string} projectId - Project UUID
   * @returns {Promise<{status: number, message: string, projectId: string}>}
   */
  removeProject: async projectId => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId
        },
        {
          params: { action: "portal_remove_project" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update project metadata (event window, description, etc.)
   * POST action=portal_update_project
   * @param {string} projectId - Project UUID
   * @param {Object} updates - Fields to update (startDate, startTime, endDate, endTime, description, name, tags)
   * @returns {Promise<{status: number, message: string, project: Object}>}
   */
  updateProject: async (projectId, updates) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          ...updates
        },
        {
          params: { action: "portal_update_project" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Upload a file to a project directory
   * POST action=portal_upload_file
   *
   * For recognised file types the backend immediately queues a background processing task
   * and returns a taskId. The frontend should call getProjectStatus() to poll progress.
   * File types that trigger auto-tasks: .gpx (GPX_CONVERT), .svx/_yolo.json (SVX_CONVERT), .mp4 (MP4_EXTRACT)
   *
   * @param {string} projectId - Project UUID
   * @param {File} file - File object to upload
   * @returns {Promise<{status: number, message: string, filename: string, size: number, taskId?: string, taskType?: string}>}
   *   taskId and taskType are present when the backend queued an auto-conversion task.
   *   Use taskId with getProjectStatus() to track progress.
   */
  uploadFile: async (projectId, file, onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append("projectId", projectId);
      formData.append("filename", file.name);
      formData.append("file", file);

      const response = await apiClient.post("", formData, {
        params: {
          action: "portal_upload_file",
          projectId
        },
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: onUploadProgress || undefined
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all files in a project directory
   * POST action=portal_list_files
   * @param {string} projectId - Project UUID
   * @returns {Promise<{status: number, message: string, totalFiles: number, totalSize: number, totalSizeFormatted: string, files: Array<{filename: string, relativePath: string, directory: string, size: number, sizeFormatted: string, extension: string, mimeType: string, creationDate: number, lastModified: number}>}>}
   */
  listFiles: async projectId => {
    try {
      const response = await apiClient.post(
        "",
        {},
        {
          params: {
            action: "portal_list_files",
            projectId
          }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List files from import_manifest.json
   * POST action=portal_list_manifest_files
   * @param {string} projectId - Project UUID
   * @param {{filterUploaded?: string}} options - Optional server-side filter (e.g. "uploaded")
   * @returns {Promise<{status: number, files: Array<{filename: string, size: number, sizeFormatted: string, extension: string, mimeType: string, lastModified: number}>}>}
   */
  listManifestFiles: async (projectId, options = {}) => {
    try {
      const params = {
        action: "portal_list_manifest_files",
        projectId
      };

      if (options.filterUploaded) {
        params.filterUploaded = options.filterUploaded;
      }

      const response = await apiClient.post(
        "",
        {},
        {
          params
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a file from a project directory
   * POST action=portal_delete_file
   * @param {string} projectId - Project UUID
   * @param {string} filename - Name of file to delete
   * @returns {Promise<{status: number, message: string, filename: string}>}
   */
  deleteFile: async (projectId, filename) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          filename
        },
        {
          params: { action: "portal_delete_file" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update a stage file in a project
   * POST action=portal_update_stagefile
   * @param {string} projectId - Project UUID
   * @param {string} filename - Name of stage file to update
   * @param {Object} stageFileContent - Updated stage file content as JSON object
   * @returns {Promise<{status: number, message: string, filename: string}>}
   */
  updateStageFile: async (projectId, filename, stageFileContent) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          filename,
          content: JSON.stringify(stageFileContent, null, 2)
        },
        {
          params: { action: "portal_update_stagefile" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Save a stagesettings file to a project directory
   * POST action=portal_save_stagesettings
   * @param {string} projectId - Project UUID
   * @param {string} filename - Name of stagesettings file (e.g. "run.stagesettings")
   * @param {Object} settingsContent - Settings content as JSON object
   * @returns {Promise<{status: number, message: string, filename: string}>}
   */
  saveStageSettings: async (projectId, filename, settingsContent) => {
    // eslint-disable-next-line no-console
    console.log(
      "[portalApi.saveStageSettings] projectId:",
      projectId,
      "filename:",
      filename
    );
    const body = {
      projectId,
      filename,
      content: JSON.stringify(settingsContent, null, 2)
    };
    // eslint-disable-next-line no-console
    console.log(
      "[portalApi.saveStageSettings] POST body:",
      JSON.stringify(body).slice(0, 300)
    );
    try {
      const response = await apiClient.post("", body, {
        params: { action: "portal_save_stagesettings" }
      });
      // eslint-disable-next-line no-console
      console.log(
        "[portalApi.saveStageSettings] response:",
        response.status,
        response.data
      );
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[portalApi.saveStageSettings] ERROR status:",
        error?.response?.status
      );
      // eslint-disable-next-line no-console
      console.error(
        "[portalApi.saveStageSettings] ERROR body (raw):",
        error?.response?.data
      );
      throw handleApiError(error);
    }
  },

  /**
   * Convert a CSV file in a project directory to H5M format (server-side)
   * POST action=portal_csv_to_h5m
   * @param {string} projectId - Project UUID
   * @param {string} filename - CSV filename in the project
   * @param {object} [options] - Optional parameters
   * @param {string} [options.outputFilename] - Output H5M filename (default: <basename>.h5m)
   * @param {string} [options.groupName] - Measurement group name (default: "signals")
   * @returns {Promise<{status: number, message: string, outputFile: string, signalCount: number, sampleCount: number}>}
   */
  csvToH5m: async (projectId, filename, options = {}) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          filename,
          ...options
        },
        {
          params: { action: "portal_csv_to_h5m" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Extract a screenshot from a video file (server-generated, cached)
   * POST action=portal_extract_screenshot
   * @param {string} projectId - Project UUID
   * @param {string} filename - Video filename or relative path
   * @param {number} second - Time offset in seconds
   * @returns {Promise<string>} Blob URL for the PNG screenshot
   */
  extractScreenshot: async (projectId, filename, second = 5) => {
    try {
      const response = await apiClient.get("", {
        params: {
          action: "portal_extract_screenshot",
          projectId,
          filename,
          second
        },
        responseType: "blob"
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Fetch a raw project file with Bearer auth and return a blob object URL.
   * Use this whenever you need to set a file URL as <video src> / <audio src>,
   * because those DOM tags cannot carry an Authorization header themselves.
   * Caller is responsible for calling URL.revokeObjectURL() when done.
   * @param {string} projectId - Project ID
   * @param {string} filename - File name inside the project
   * @returns {Promise<string>} Blob object URL
   */
  fetchFileAsObjectUrl: async (projectId, filename) => {
    try {
      const response = await apiClient.get("", {
        params: {
          action: "portal_get_file",
          projectId,
          filename
        },
        responseType: "blob"
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all signals in an H5M file
   * GET action=list_h5m_signals
   * @param {string} projectId - Project UUID
   * @param {string} filename - H5M file name
   * @returns {Promise<{signals: Array<{name: string, unit: string, dataPoints: number, sampleRate: number}>}>}
   */
  listH5mSignals: async (projectId, filename) => {
    try {
      const response = await apiClient.get("", {
        params: {
          action: "list_h5m_signals",
          projectId,
          filename
        }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Evaluate signal marker rules against H5M data server-side
   * POST action=evaluate_signal_markers
   * @param {string} projectId - Project UUID
   * @param {string} h5mFilename - H5M file name
   * @param {Array<{id: string, action: string}>} rules - Signal rules with action strings (e.g. "PITCH>=50")
   * @param {Array<{id: string, action: string}>} [stacked] - Stacked rules with action strings (e.g. "pitch AND roll")
   * @returns {Promise<{results: Object, cached: boolean, evaluationTimeMs: number}>}
   */
  evaluateSignalMarkers: async (
    projectId,
    h5mFilename,
    rules,
    stacked = []
  ) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          h5mFilename,
          rules,
          stacked
        },
        {
          params: { action: "evaluate_signal_markers" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get detection frames from an SVX (StageVXtract) file, optionally filtered to a time window
   * GET action=portal_get_svx_frames
   * @param {string} projectId - Project UUID
   * @param {string} filename - SVX file name in the project directory
   * @param {number|null} fromSec - Start of time window in seconds (inclusive), or null for all
   * @param {number|null} toSec - End of time window in seconds (inclusive), or null for all
   * @returns {Promise<{frames: Array, frameCount: number}>}
   */
  getSvxFrames: async (projectId, filename, fromSec = null, toSec = null) => {
    try {
      const params = { action: "portal_get_svx_frames", projectId, filename };
      if (fromSec !== null) params.fromSec = fromSec;
      if (toSec !== null) params.toSec = toSec;
      const response = await apiClient.get("", { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * AI-assisted signal marker chat
   * POST action=ai_marker_chat
   * @param {string} projectId - Project UUID
   * @param {string} h5mFilename - H5M file name
   * @param {Array<{role: string, content: string}>} messages - Chat messages
   * @param {Array<{name: string, action: string}>} [existingMarkers] - Current markers for context
   * @returns {Promise<{reply: string, action: Object, markers: Array, stats: Object, usage: Object, cached: boolean, evaluationTimeMs: number}>}
   */
  aiMarkerChat: async (
    projectId,
    h5mFilename,
    messages,
    existingMarkers = []
  ) => {
    try {
      const response = await apiClient.post(
        "",
        {
          projectId,
          h5mFilename,
          messages,
          existingMarkers
        },
        {
          params: { action: "ai_marker_chat" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Save a named rule to the user's rule library
   * POST action=save_rule_library
   * @param {string} name - Rule display name
   * @param {string} action - Action string to save
   * @param {string} [description] - Human-readable description
   * @param {string[]} [tags] - Tags for filtering
   * @param {boolean} [override] - Overwrite existing rule with the same name
   * @returns {Promise<{rule: Object}>}
   */
  saveRuleLibrary: async (
    name,
    action,
    description = "",
    tags = [],
    override = false
  ) => {
    try {
      const body = { name, action, description, tags };
      if (override) body.override = true;
      const response = await apiClient.post("", body, {
        params: { action: "save_rule_library" }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all saved rules for the logged-in user
   * POST action=list_rule_library
   * @param {string[]} [tags] - Optional tag filter (AND logic)
   * @returns {Promise<{rules: Array}>}
   */
  listRuleLibrary: async (tags = []) => {
    try {
      const body = {};
      if (tags.length > 0) body.tags = tags;
      const response = await apiClient.post("", body, {
        params: { action: "list_rule_library" }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a saved rule by ID
   * POST action=delete_rule_library
   * @param {string} ruleId - Rule UUID
   * @returns {Promise<{message: string, ruleId: string}>}
   */
  deleteRuleLibrary: async ruleId => {
    try {
      const response = await apiClient.post(
        "",
        { ruleId },
        {
          params: { action: "delete_rule_library" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List shared rules for the active scenario
   * POST action=portal_list_shared_rules
   * @returns {Promise<{rules: Array}>}
   */
  listSharedRules: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        { params: { action: "portal_list_shared_rules" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Save a rule to the shared scenario library (scenario admin only)
   * POST action=portal_save_shared_rule
   * @param {string} name - Rule display name
   * @param {string} action - Action string
   * @param {string} [description] - Human-readable description
   * @param {string[]} [tags] - Tags for filtering
   * @returns {Promise<{status: number, message: string, id: string}>}
   */
  saveSharedRule: async (name, action, description = "", tags = []) => {
    try {
      const response = await apiClient.post(
        "",
        { name, action, description, tags },
        { params: { action: "portal_save_shared_rule" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a shared rule (scenario admin only)
   * POST action=portal_delete_shared_rule
   * @param {string} id - Rule UUID
   * @returns {Promise<{status: number, message: string}>}
   */
  deleteSharedRule: async id => {
    try {
      const response = await apiClient.post(
        "",
        { id },
        { params: { action: "portal_delete_shared_rule" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Load marker settings for a project from portal storage
   * GET action=portal_get_file with filename=project.markersettings
   * @param {string} projectId - Project UUID
   * @returns {Promise<{markers: Array, savedAt: string, version: string}>} The markersettings JSON content, or null if not found
   */
  getProjectMarkerSettings: async projectId => {
    // eslint-disable-next-line no-console
    console.log(
      "[portalApi] getProjectMarkerSettings called with projectId:",
      projectId
    );
    try {
      const response = await apiClient.get("", {
        params: {
          action: "portal_get_file",
          projectId,
          filename: "project.markersettings"
        },
        // Receive as text so we can JSON.parse it ourselves
        responseType: "text"
      });
      // portal_get_file returns raw binary/text content — parse it as JSON
      const text =
        typeof response.data === "string"
          ? response.data
          : JSON.stringify(response.data);
      return JSON.parse(text);
    } catch (error) {
      // 404 means no markersettings file yet — return null gracefully
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw handleApiError(error);
    }
  },

  /**
   * Save all marker definitions for a project.
   * Serializes markers to JSON and uploads as project.markersettings
   * using the existing portal_upload_file endpoint.
   * @param {string} projectId - Project UUID
   * @param {Array} markers - All marker definitions to persist
   * @returns {Promise<{status: number, message: string, filename: string, size: number}>}
   */
  saveAllSignalMarkers: async (projectId, markers) => {
    // eslint-disable-next-line no-console
    console.log(
      "[portalApi] saveAllSignalMarkers called — projectId:",
      projectId,
      "markers:",
      JSON.stringify(markers, null, 2)
    );
    try {
      const payload = JSON.stringify(
        {
          markers,
          savedAt: new Date().toISOString(),
          version: "1.0"
        },
        null,
        2
      );
      const blob = new Blob([payload], { type: "application/json" });
      const formData = new FormData();
      formData.append("projectId", projectId);
      formData.append("filename", "project.markersettings");
      formData.append("file", blob, "project.markersettings");
      const response = await apiClient.post("", formData, {
        params: {
          action: "portal_upload_file",
          projectId,
          filename: "project.markersettings"
        },
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data;
    } catch (error) {
      console.error(
        "[portalApi] saveAllSignalMarkers: upload FAILED:",
        error?.response?.status,
        error?.response?.data || error?.message
      );
      throw handleApiError(error);
    }
  },

  // ─── Scenario management ─────────────────────────────────────────────────

  /**
   * List scenarios for the current user. Sysadmin receives all scenarios.
   * POST action=portal_list_scenarios
   * @returns {Promise<{status: number, scenarios: Array<{name, role}>, activeScenario: string}>}
   */
  listScenarios: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        { params: { action: "portal_list_scenarios" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Switch the active scenario for the current session.
   * POST action=portal_switch_scenario
   * @param {string} scenario - Scenario name to switch to
   * @returns {Promise<{status: number, message: string, activeScenario: string, scenarioRole: string}>}
   */
  switchScenario: async scenario => {
    try {
      const response = await apiClient.post(
        "",
        { scenario },
        { params: { action: "portal_switch_scenario" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a new scenario (admin/sysadmin only).
   * POST action=portal_create_scenario
   * @param {string} scenario - Scenario name (slug, no spaces)
   * @param {string} description - Optional description
   * @returns {Promise<{status: number, message: string, scenario: string}>}
   */
  createScenario: async (scenario, description = "") => {
    try {
      const response = await apiClient.post(
        "",
        { scenario, description },
        { params: { action: "portal_create_scenario" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Add an existing account to a scenario with a given role.
   * POST action=portal_add_user_to_scenario
   * @param {string} username - Account to add
   * @param {string} scenario - Target scenario name
   * @param {string} role     - "member" | "admin"
   * @returns {Promise<{status: number, message: string}>}
   */
  addUserToScenario: async (username, scenario, role = "member") => {
    try {
      const response = await apiClient.post(
        "",
        { username, scenario, role },
        { params: { action: "portal_add_user_to_scenario" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Remove a user from a scenario.
   * POST action=portal_remove_user_from_scenario
   * @param {string} username - Account to remove
   * @param {string} scenario - Scenario name
   * @returns {Promise<{status: number, message: string}>}
   */
  removeUserFromScenario: async (username, scenario) => {
    try {
      const response = await apiClient.post(
        "",
        { username, scenario },
        { params: { action: "portal_remove_user_from_scenario" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all users in a scenario.
   * POST action=portal_list_scenario_users
   * @param {string} scenario - Scenario name (defaults to active if omitted)
   * @returns {Promise<{status: number, users: Array<{username, role}>}>}
   */
  listScenarioUsers: async scenario => {
    try {
      const response = await apiClient.post(
        "",
        { scenario },
        { params: { action: "portal_list_scenario_users" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ─── Account settings ─────────────────────────────────────────────────────

  /**
   * Change the current user's password.
   * POST action=portal_change_password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword     - New password
   * @returns {Promise<{status: number, message: string}>}
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiClient.post(
        "",
        { currentPassword, newPassword },
        { params: { action: "portal_change_password" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ─── Admin / user management ─────────────────────────────────────────────

  /**
   * List all accounts with status=pending in the current scenario.
   * POST action=portal_list_pending_accounts
   * @returns {Promise<{status: number, accounts: Array<{username, email, createdAt}>}>}
   */
  listPendingAccounts: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        { params: { action: "portal_list_pending_accounts" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * List all accounts in the system (sysadmin only).
   * POST action=portal_list_all_accounts
   * @returns {Promise<{status: number, accounts: Array<{username, email, systemRole, status, scenarios}>}>}
   */
  listAllAccounts: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        { params: { action: "portal_list_all_accounts" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Approve a pending account (set status → active).
   * POST action=portal_approve_account
   * @param {string} username - Account to approve
   * @returns {Promise<{status: number, message: string}>}
   */
  approveAccount: async username => {
    try {
      const response = await apiClient.post(
        "",
        { username },
        { params: { action: "portal_approve_account" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Set account status (active | disabled).
   * POST action=portal_set_account_status
   * @param {string} username - Target account
   * @param {string} status   - "active" | "disabled"
   * @returns {Promise<{status: number, message: string}>}
   */
  setAccountStatus: async (username, status) => {
    try {
      const response = await apiClient.post(
        "",
        { username, status },
        { params: { action: "portal_set_account_status" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get background task queue for a project.
   * Poll this after any upload or scan to track processing progress.
   * GET action=portal_project_status
   *
   * The server does not always compute minY/maxY for all signal types in H5M metadata
   * (e.g. speed, heart rate from GPX arrive as null). These signals become invisible in
   * the DataViewer without valid min/max. This endpoint is used by the polling loop so
   * the frontend can show progress while conversion/extraction tasks run in the background.
   *
   * Recommended strategy: poll every 2 s while active !== null, drop to 30 s when idle.
   *
   * @param {string} projectId - Project ID in {scenario}_{uuid4} format
   * @returns {Promise<{
   *   status: number,
   *   projectId: string,
   *   active: {id: string, type: string, status: string, progress: number, message: string, sourceFile: string, queuedAt: string, startedAt: string} | null,
   *   queue: Array<{id: string, type: string, status: string, progress: number, message: string, sourceFile: string, queuedAt: string, startedAt?: string, completedAt?: string}>
   * }>}
   */
  getProjectStatus: async projectId => {
    try {
      const response = await apiClient.get("", {
        params: { action: "portal_project_status", projectId }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Scan a project for MP4 files not yet in import_manifest.json and queue MP4_EXTRACT tasks.
   * Call this alongside portal_list_files when opening a project. Returns immediately — extraction runs in background.
   * GET action=portal_scan_project
   * @param {string} projectId - Project ID in {scenario}_{uuid4} format
   * @param {boolean} [force=false] - Re-extract even if already in manifest
   * @returns {Promise<{status: number, queued: number, skipped: number, taskIds: string[]}>}
   */
  scanProject: async (projectId, force = false) => {
    try {
      const response = await apiClient.get("", {
        params: { action: "portal_scan_project", projectId, force }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * GET action=portal_project_timeline
   * Returns pre-computed offsetSeconds for every file in the project.
   * The frontend never needs to do epoch arithmetic — drop offsetSeconds onto
   * the timeline/stage file builder directly.
   *
   * Slot context: pass `projectId@slotId` to get a slot-filtered, slot-merged timeline.
   * The response always includes `slots` (all slots in the project) and `activeSlot`
   * (null for projectslot, slot object for a named slot).
   *
   * @param {string} projectId  Plain project ID or "projectId@slotId"
   * @param {string} [rule='epoch']
   * @returns {Promise<{status, projectId, activeSlot, rule, projectZeroUtc, slots, files}>}
   */
  getProjectTimeline: async (projectId, rule = "epoch") => {
    try {
      const response = await apiClient.get("", {
        params: { action: "portal_project_timeline", projectId, rule }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Save a manual timeline offset for a file. Supports three modes:
   *  - Mode 1: { offsetSeconds }          — direct seconds from projectZeroUtc
   *  - Mode 2: { utcStart }               — ISO-8601 UTC, backend does the math
   *  - Mode 3: { relativeToFile, relativeOffsetSeconds } — relative to another file
   *  - Clear:  { clear: true }            — removes manual override
   * Optional: { note } for auditing.
   */
  saveTimelineOffset: async (projectId, filename, payload) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, filename, ...payload },
        { params: { action: "portal_save_timeline_offset" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST/GET action=portal_manage_slots
   * CRUD for project timeslot manifests.
   *
   * method='create'      { name, description }         → { slotId, name, description, fileCount }
   * method='rename'      { slotId, name, description } → { slotId, name, description }
   * method='delete'      { slotId }                    → { deleted }
   * method='add_file'    { slotId, filename }           → { slotId, filename, added }
   * method='remove_file' { slotId, filename }           → { slotId, filename, removed }
   * method='list'        (GET)                          → { slots: [...] }
   *
   * @param {string} projectId Plain project ID (no @slotId suffix)
   * @param {string} method    One of the methods above
   * @param {object} [params]  Additional params depending on method
   */
  manageSlots: async (projectId, method, params = {}) => {
    try {
      if (method === "list") {
        const response = await apiClient.get("", {
          params: { action: "portal_manage_slots", projectId, method }
        });
        return response.data;
      }
      const response = await apiClient.post(
        "",
        { projectId, method, ...params },
        { params: { action: "portal_manage_slots" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_align_videos_audio
   * Cross-correlates audio tracks to find the time offset of candidateFiles
   * relative to referenceFile. Enqueues a background AUDIO_ALIGN task.
   * Poll portal_project_status for progress; when done, read result from
   * portal_project_timeline (audioAlignLagSeconds, audioAlignConfidence, audioAlignApplied).
   * @param {string} projectId
   * @param {string} referenceFile - Filename of the known-good reference clip
   * @param {string[]} files - Candidate filenames to align
   * @param {object} [opts] - Optional: { maxDurationSec, maxOffsetSec, autoApply }
   */
  alignVideosAudio: async (projectId, referenceFile, files, opts = {}) => {
    try {
      const body = {
        projectId,
        referenceFile,
        files,
        autoApply: true,
        ...opts
      };
      console.log("[AudioAlign] API call portal_align_videos_audio", body);
      const response = await apiClient.post("", body, {
        params: { action: "portal_align_videos_audio" }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_exchange_share_token
   * Validates the share JWT (sent as Bearer) and sets a short-lived httpOnly
   * `st` cookie on the response. The `st` cookie is then automatically sent
   * by the browser on `<video src>` / `<audio src>` requests so the backend
   * can authorise media streaming without needing an Authorization header.
   * Call this once at player boot when a ?shareToken= is present in the URL.
   */
  exchangeShareToken: async () => {
    try {
      const response = await apiClient.post(
        "",
        {},
        { params: { action: "portal_exchange_share_token" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_set_stage_share_token
   * Mints a share token scoped to a specific stage file and stores it in the
   * registry. Revokes any previous token for that stage file automatically.
   */
  setStageShareToken: async (
    projectId,
    stageFile,
    expiresInDays = 30,
    expiresInMinutes = null
  ) => {
    try {
      const body =
        expiresInMinutes != null
          ? { projectId, stageFile, expiresInMinutes }
          : { projectId, stageFile, expiresInDays };
      const response = await apiClient.post("", body, {
        params: { action: "portal_set_stage_share_token" }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * GET action=portal_get_stage_share_token
   * Returns the active share token record for the stage file, or null if none.
   */
  getStageShareToken: async (projectId, stageFile) => {
    try {
      const response = await apiClient.post(
        "",
        {},
        {
          params: {
            action: "portal_get_stage_share_token",
            projectId,
            stageFile
          }
        }
      );
      // Return null if the body has no shareToken (handles backends that return
      // HTTP 200 with { status: 404 } in the body instead of HTTP 404).
      return response.data?.shareToken ? response.data : null;
    } catch (error) {
      // 404 means no token — return null instead of throwing
      if (error.status === 404 || error.response?.status === 404) return null;
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_remove_stage_share_token
   * Revokes and removes the share token for the stage file.
   */
  removeStageShareToken: async (projectId, stageFile) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, stageFile },
        { params: { action: "portal_remove_stage_share_token" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get scenario config (scenario-config.json) for a given scenario.
   * GET action=portal_get_scenario_config
   */
  getScenarioConfig: async scenario => {
    try {
      const response = await apiClient.get("", {
        params: { action: "portal_get_scenario_config", scenario }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Partial-update scenario config (sysadmin or scenario admin only).
   * POST action=portal_set_scenario_config
   * Body may include: displayName, aiPrompt, units, features, converters.
   */
  setScenarioConfig: async (scenario, update) => {
    try {
      const response = await apiClient.post(
        "",
        { scenario, ...update },
        { params: { action: "portal_set_scenario_config" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ─── Activity log ─────────────────────────────────────────────────────────

  /**
   * POST action=portal_get_activity_log
   * Returns activity log entries for a project, newest first.
   * @param {string} projectId
   * @param {object} [filters]
   * @param {string} [filters.file]      - Filter by target.path exact match
   * @param {string} [filters.eventType] - Filter by event_type prefix (e.g. "pipeline.")
   * @returns {Promise<{status: number, entries: Array}>}
   */
  getActivityLog: async (projectId, filters = {}) => {
    try {
      const body = { projectId, ...filters };
      const response = await apiClient.post("", body, {
        params: { action: "portal_get_activity_log" }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_get_file_checklist
   * Returns expected pipeline steps (from scenario config) merged with latest activity log status.
   * @param {string} projectId
   * @param {string} file - Filename (e.g. "rc01.mp4")
   * @returns {Promise<{status: number, file: string, scenario: string, steps: Array<{event_type, status, timestamp, errorId}>}>}
   */
  getFileChecklist: async (projectId, file) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, file },
        { params: { action: "portal_get_file_checklist" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_get_error_log
   * Returns raw text content of an error log file for a failed activity.
   * @param {string} projectId
   * @param {string} errorId - UUID from the activity log entry's errorId field
   * @returns {Promise<string>} Raw error log text
   */
  getErrorLog: async (projectId, errorId) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, errorId },
        {
          params: { action: "portal_get_error_log" },
          responseType: "text"
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_update_file_meta
   * Update metadata for a single file in a project (e.g. cameraId).
   * @param {string} projectId
   * @param {string} filename
   * @param {object} meta  - Partial metadata to update (e.g. { cameraId: "cam01" })
   * @returns {Promise<{status: number}>}
   */
  updateFileMeta: async (projectId, filename, meta) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, filename, ...meta },
        { params: { action: "portal_update_file_meta" } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * POST action=portal_run_manual_step
   * Triggers a manual pipeline operation on a file already in a project.
   * @param {string} projectId
   * @param {string} filename
   * @param {string} operationId - e.g. "thumbnail_create" | "thumbnail_remove"
   * @returns {Promise<{status: number, pipelineRunId: string, operationId: string}>}
   */
  runManualStep: async (projectId, filename, operationId) => {
    try {
      const response = await apiClient.post(
        "",
        {},
        {
          params: {
            action: "portal_run_manual_step",
            projectId,
            filename,
            operationId
          }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get wizard state for a project
   * POST action=portal_get_wizard
   * @param {string} projectId - Project UUID
   * @returns {Promise<{status: number, wizard: Object}>}
   */
  getWizard: async projectId => {
    try {
      const response = await apiClient.post(
        "",
        { projectId },
        {
          params: { action: "portal_get_wizard" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update wizard step progress
   * POST action=portal_update_wizard
   * @param {string} projectId - Project UUID
   * @param {number} currentStep - Current step index
   * @param {string} stepId - Step identifier
   * @param {string} status - Step status (not-started, in-progress, completed, skipped)
   * @returns {Promise<{status: number, message: string}>}
   */
  updateWizard: async (projectId, currentStep, stepId, status) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, currentStep, stepId, status },
        {
          params: { action: "portal_update_wizard" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Complete wizard
   * POST action=portal_complete_wizard
   * @param {string} projectId - Project UUID
   * @returns {Promise<{status: number, message: string}>}
   */
  completeWizard: async projectId => {
    try {
      const response = await apiClient.post(
        "",
        { projectId },
        {
          params: { action: "portal_complete_wizard" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get aggregated project activity statistics
   * GET action=portal_project_activity
   * Returns overall progress across all active tasks for display in wizard header
   * @param {string} projectId - Project UUID
   * @returns {Promise<{status: number, projectId: string, active: boolean, overallProgress: number, activeTasks: number, queuedTasks: number, summary: string|null}>}
   */
  getProjectActivity: async projectId => {
    try {
      const response = await apiClient.get("", {
        params: {
          action: "portal_project_activity",
          projectId
        }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get event model analysis for wizard sources page
   * POST action=portal_get_eventmodel
   * Returns analyzed and grouped sources from the manifest with timing information
   * @param {string} projectId - Project UUID
   * @returns {Promise<{
   *   status: number,
   *   videoSources: Array<{id: string, name: string, clips: number, hasAudio: boolean, hasTimestamp: boolean, timeQuality: string}>,
   *   dataSources: Array<{id: string, name: string, datasets: number, hasTimestamp: boolean, timeQuality: string}>,
   *   otherFiles: Array<{id: string, name: string, hasTimestamp: boolean}>,
   *   timing: {assetsWithTime: number, totalAssets: number, suggestedReference: string, suggestedReferenceReason: string},
   *   visual: {suggestedReference: string, suggestedReferenceReason: string},
   *   audio: {sourcesWithAudio: number, autoSyncAvailable: boolean},
   *   outsideEventPeriod: {count: number, files: Array<string>}
   * }>}
   */
  getEventModel: async projectId => {
    try {
      const response = await apiClient.get("", {
        params: { action: "portal_get_eventmodel", projectId }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update event model (rename sources)
   * @param {string} projectId - Project ID
   * @param {Array<{sourceId: string, name: string}>} updates - Array of source updates
   * @returns {Promise<{status: number, message: string, updatedCount: number, updatedAt: string}>}
   */
  updateEventModel: async (projectId, updates) => {
    try {
      const response = await apiClient.post(
        "",
        { projectId, updates },
        {
          params: { action: "portal_update_eventmodel" }
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export default portalApi;
